/**
 * useDailyTheme, single source of truth para a sincronização temática.
 * Faz 1 GET /api/today e compartilha o resultado via cache de módulo.
 * Componentes que precisarem do tema do dia chamam este hook e leem o que
 * for relevante (devocional, versículo, frase, capítulos, plano relacionado).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let _cache = null;
let _cacheDate = null;
let _inflight = null;
const _subs = new Set();

function _todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function _broadcast() {
  _subs.forEach((fn) => fn(_cache));
}

async function _fetch() {
  if (_inflight) return _inflight;
  // Timeout de 8s para evitar skeleton infinito quando o backend está saturado.
  // Em saturação, o componente ainda pode mostrar fallback amistoso.
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), 8000);
  _inflight = api
    .get("/today", { signal: controller.signal })
    .then(({ data }) => {
      _cache = data;
      _cacheDate = _todayKey();
      _broadcast();
      return data;
    })
    .catch(() => {
      _cache = { error: true };
      _broadcast();
      return _cache;
    })
    .finally(() => {
      clearTimeout(t);
      _inflight = null;
    });
  return _inflight;
}

export default function useDailyTheme() {
  const [data, setData] = useState(_cache);

  useEffect(() => {
    const sub = (v) => setData(v);
    _subs.add(sub);
    // Invalida cache se o dia (UTC) mudou desde o último fetch
    if (!_cache || _cacheDate !== _todayKey()) {
      _cache = null;
      _fetch();
    }
    return () => _subs.delete(sub);
  }, []);

  return {
    theme: data && !data.error ? data : null,
    loading: !data,
    error: !!(data && data.error),
  };
}
